import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { storesApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Boxes, AlertTriangle, Plus, Loader2, Store, CalendarOff, Trash2 } from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StoresExpiryPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [tab, setTab] = useState('expiry');
  const [alerts, setAlerts] = useState(null);
  const [stores, setStores] = useState([]);
  const [batches, setBatches] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeDialog, setStoreDialog] = useState(false);
  const [batchDialog, setBatchDialog] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    try {
      const [a, s, b, h] = await Promise.all([
        storesApi.expiryAlerts(pid, 30), storesApi.list(pid), storesApi.listBatches(pid), storesApi.listHolidays(pid),
      ]);
      setAlerts(a.data); setStores(s.data || []); setBatches(b.data || []); setHolidays(h.data || []);
    } catch (e) { toast.error('Failed to load stores data'); }
    finally { setLoading(false); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const addHoliday = async () => {
    const d = prompt('Holiday date (YYYY-MM-DD):'); if (!d) return;
    const name = prompt('Holiday name:') || '';
    try { await storesApi.addHoliday(pid, { holiday_date: d, name }); toast.success('Holiday added'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delHoliday = async (id) => { try { await storesApi.deleteHoliday(id); load(); } catch (e) { toast.error('Failed'); } };

  if (!currentProperty) return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center"><Boxes className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-bold text-slate-900">Stores &amp; Expiry</h1><p className="text-sm text-slate-500">Multi-store inventory, batch expiry alerts &amp; holiday operation</p></div>
        </div>
      </div>

      {/* Expiry KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-red-500" /><div><p className="text-xs text-slate-500">Expired</p><p className="text-xl font-semibold text-red-600">{alerts?.expired_count ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-amber-500" /><div><p className="text-xs text-slate-500">Expiring (30d)</p><p className="text-xl font-semibold text-amber-600">{alerts?.expiring_count ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Boxes className="h-5 w-5 text-slate-500" /><div><p className="text-xs text-slate-500">Value at Risk</p><p className="text-xl font-semibold">{ETB(alerts?.value_at_risk)}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Store className="h-5 w-5 text-blue-500" /><div><p className="text-xs text-slate-500">Stores</p><p className="text-xl font-semibold">{stores.length}</p></div></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="expiry">Expiry Alerts</TabsTrigger>
          <TabsTrigger value="batches">Batches</TabsTrigger>
          <TabsTrigger value="stores">Stores</TabsTrigger>
          <TabsTrigger value="holidays">Holidays</TabsTrigger>
        </TabsList>

        <TabsContent value="expiry">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Batch</TableHead><TableHead>Store</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Expiry</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  : (alerts?.alerts || []).length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No items expiring soon.</TableCell></TableRow>
                  : alerts.alerts.map((a) => (
                    <TableRow key={a.batch_id}>
                      <TableCell className="font-medium">{a.item}</TableCell>
                      <TableCell className="font-mono text-xs">{a.batch_number}</TableCell>
                      <TableCell>{a.location || '—'}</TableCell>
                      <TableCell className="text-right">{a.quantity}</TableCell>
                      <TableCell>{a.expiry_date}</TableCell>
                      <TableCell><Badge className={a.status === 'EXPIRED' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}>{a.status === 'EXPIRED' ? `Expired ${-a.days_to_expiry}d ago` : `In ${a.days_to_expiry}d`}</Badge></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="batches" className="space-y-3">
          <div className="flex justify-end"><Button onClick={() => setBatchDialog(true)}><Plus className="h-4 w-4 mr-2" /> Receive Batch</Button></div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Batch</TableHead><TableHead>Store</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Cost</TableHead><TableHead>Received</TableHead><TableHead>Expiry</TableHead></TableRow></TableHeader>
              <TableBody>
                {batches.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No batches.</TableCell></TableRow>
                  : batches.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>{b.inventory_items?.name || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{b.batch_number}</TableCell>
                      <TableCell>{b.storage_locations?.name || '—'}</TableCell>
                      <TableCell className="text-right">{b.quantity}</TableCell>
                      <TableCell className="text-right">{ETB(b.unit_cost)}</TableCell>
                      <TableCell>{b.received_date}</TableCell>
                      <TableCell>{b.expiry_date || '—'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="stores" className="space-y-3">
          <div className="flex justify-end"><Button variant="outline" onClick={() => setStoreDialog(true)}><Plus className="h-4 w-4 mr-2" /> Add Store</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {stores.map((s) => (
              <Card key={s.id}><CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div><p className="font-semibold">{s.name}</p><p className="text-xs font-mono text-slate-500">{s.code}</p></div>
                  <Badge variant="outline">{s.location_type || 'MAIN'}</Badge>
                </div>
                <p className="text-xs mt-2">{s.operates_on_holidays ? '✅ Operates on holidays' : '— Closed on holidays'}</p>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="holidays" className="space-y-3">
          <div className="flex justify-end"><Button variant="outline" onClick={addHoliday}><CalendarOff className="h-4 w-4 mr-2" /> Add Holiday</Button></div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {holidays.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">No holidays set. Mini-stores still operate on holidays.</TableCell></TableRow>
                  : holidays.map((h) => (
                    <TableRow key={h.id}><TableCell>{h.holiday_date}</TableCell><TableCell>{h.name}</TableCell><TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => delHoliday(h.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell></TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <StoreDialog open={storeDialog} onOpenChange={setStoreDialog} pid={pid} onSaved={() => { setStoreDialog(false); load(); }} />
      <BatchDialog open={batchDialog} onOpenChange={setBatchDialog} pid={pid} batches={batches} stores={stores} onSaved={() => { setBatchDialog(false); load(); }} />
    </div>
  );
}

function StoreDialog({ open, onOpenChange, pid, onSaved }) {
  const [name, setName] = useState(''); const [type, setType] = useState('MAIN'); const [holiday, setHoliday] = useState(false); const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(''); setType('MAIN'); setHoliday(false); } }, [open]);
  const save = async () => {
    if (!name.trim()) { toast.error('Enter a name'); return; }
    try { setSaving(true); await storesApi.create(pid, { name, location_type: type, operates_on_holidays: holiday }); toast.success('Store added'); onSaved(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Add Store</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Type</Label><Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MAIN">Main Store</SelectItem><SelectItem value="MINI">Mini Store</SelectItem></SelectContent></Select></div>
        <div className="flex items-center gap-2"><Checkbox id="hol" checked={holiday} onCheckedChange={(c) => setHoliday(!!c)} /><Label htmlFor="hol">Operates on holidays</Label></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function BatchDialog({ open, onOpenChange, pid, batches, stores, onSaved }) {
  // build item list from existing batches' items (or allow free item id) — use distinct items from batches
  const items = Array.from(new Map((batches || []).filter((b) => b.item_id).map((b) => [b.item_id, b.inventory_items?.name || b.item_id])).entries());
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ item_id: '', location_id: '', batch_number: '', quantity: '', unit_cost: '', expiry_date: '' }); }, [open]);
  const save = async () => {
    if (!f.item_id) { toast.error('Select an item'); return; }
    try {
      setSaving(true);
      await storesApi.createBatch(pid, { item_id: f.item_id, location_id: f.location_id || null, batch_number: f.batch_number || null, quantity: parseFloat(f.quantity) || 0, unit_cost: parseFloat(f.unit_cost) || 0, expiry_date: f.expiry_date || null });
      toast.success('Batch received'); onSaved();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Receive Batch</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Item</Label>
          <Select value={f.item_id} onValueChange={(v) => setF({ ...f, item_id: v })}><SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
            <SelectContent>{items.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent></Select>
          {items.length === 0 && <p className="text-xs text-slate-500">No perishable items yet — add items in Inventory first.</p>}
        </div>
        <div className="space-y-1.5"><Label>Store</Label>
          <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}><SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={f.quantity || ''} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Unit Cost</Label><Input type="number" value={f.unit_cost || ''} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Batch No</Label><Input value={f.batch_number || ''} onChange={(e) => setF({ ...f, batch_number: e.target.value })} placeholder="auto" /></div>
          <div className="space-y-1.5"><Label>Expiry Date</Label><Input type="date" value={f.expiry_date || ''} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>Receive</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
